"use client";

import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { NativeLink as Link } from "@/components/native-link";
import {
  ArrowLeft, CheckCircle2, ClipboardCheck, Clock3, ExternalLink,
  Link2, Loader2, Trash2, XCircle,
} from "lucide-react";
import { Card, PageHeader, Pill } from "@/components/pulse-shell";
import { api, type CheckupProtocolMeta, type CheckupResult } from "@/lib/api";
import {
  CHECKUP_CATEGORY_LABEL,
  getCheckupProtocol,
} from "@/lib/checkup-protocols";

function resultDeepLink(protocolId: string, resultId: string): string {
  const path = `/checkups/${encodeURIComponent(protocolId)}?result=${encodeURIComponent(resultId)}`;
  if (typeof window === "undefined") return path;
  return `${window.location.origin}${path}`;
}

export function CheckupDetailPage() {
  const { id = "" } = useParams();
  const [searchParams] = useSearchParams();
  const focusResultId = searchParams.get("result")?.trim() || "";
  const navigate = useNavigate();
  const protocol = getCheckupProtocol(id);
  const focusedRowRef = useRef<HTMLDivElement | null>(null);

  const [meta, setMeta] = useState<CheckupProtocolMeta | null>(null);
  const [results, setResults] = useState<CheckupResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<"pass" | "fail" | null>(null);
  const [sessionId, setSessionId] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  async function load() {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.getCheckup(id);
      setMeta(data.meta);
      setResults(data.results || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load checkup history");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, [id]);

  useEffect(() => {
    if (loading || !focusResultId) return;
    const el = focusedRowRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [loading, focusResultId, results]);

  if (!protocol) {
    return (
      <div className="empty-state" style={{ marginTop: 40 }}>
        Protocol not found. <Link href="/checkups">Back to Checkups</Link>
      </div>
    );
  }

  const latest = meta?.latestStatus || null;

  async function submit(status: "pass" | "fail") {
    const trimmed = notes.trim();
    if (!trimmed) {
      setError("Describe what you saw or did before saving.");
      return;
    }
    setSaving(status);
    setError(null);
    try {
      await api.saveCheckupResult(protocol!.id, {
        status,
        sessionId: sessionId.trim() || undefined,
        notes: trimmed,
      });
      setNotes("");
      setSessionId("");
      setToast(status === "pass" ? "Recorded as Pass" : "Recorded as Fail — protocol marked red");
      setTimeout(() => setToast(null), 3200);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(null);
    }
  }

  async function removeResult(resultId: string) {
    if (!confirm("Delete this checkup result?")) return;
    try {
      await api.deleteCheckupResult(protocol!.id, resultId);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Delete failed");
    }
  }

  async function copyResultLink(resultId: string) {
    const url = resultDeepLink(protocol!.id, resultId);
    try {
      await navigator.clipboard.writeText(url);
      setToast("Result link copied");
      setTimeout(() => setToast(null), 2800);
    } catch {
      setError("Could not copy link — copy from the address bar after opening the result.");
    }
  }

  const focusedMissing =
    !loading && !!focusResultId && !results.some((r) => r.resultId === focusResultId);

  return <>
    <PageHeader
      eyebrow={<><Link href="/checkups">Checkups</Link> / {CHECKUP_CATEGORY_LABEL[protocol.category]}</>}
      title={protocol.title}
      subtitle={protocol.goal}
      actions={<>
        <button type="button" className="btn" onClick={() => navigate("/checkups")}>
          <ArrowLeft />All protocols
        </button>
        {latest === "fail" && <Pill tone="red"><XCircle size={12} />Currently failing</Pill>}
        {latest === "pass" && <Pill tone="green"><CheckCircle2 size={12} />Currently passing</Pill>}
        {!latest && <Pill tone="gray">Never run</Pill>}
      </>}
    />

    {focusedMissing && (
      <div className="checkup-focus-missing" role="status">
        Deep-linked result <code>{focusResultId}</code> was not found in this protocol’s history
        (deleted or wrong id). Protocol page still works — pick another run below.
      </div>
    )}

    <div className={`checkup-detail-banner status-${latest || "never"}`}>
      <div>
        <span className="eyebrow">{CHECKUP_CATEGORY_LABEL[protocol.category]} · {protocol.priority.toUpperCase()}</span>
        <h2>{latest === "fail" ? "Latest result failed" : latest === "pass" ? "Latest result passed" : "Ready for first checkup"}</h2>
        <p>
          {meta?.lastResultAt
            ? `Last run ${new Date(meta.lastResultAt).toLocaleString()} · ${meta.passCount} pass / ${meta.failCount} fail`
            : "Open the links, follow the steps, then record Pass or Fail with a session ID and notes."}
        </p>
      </div>
      <div className="checkup-detail-stats">
        <div><b>{protocol.estimatedMinutes}m</b><span>Est. time</span></div>
        <div><b>{results.length}</b><span>Runs</span></div>
        <div><b>{meta?.passCount ?? 0}</b><span>Passes</span></div>
        <div><b>{meta?.failCount ?? 0}</b><span>Fails</span></div>
      </div>
    </div>

    <div className="checkup-detail-grid">
      <div className="stack">
        <Card title="Protocol steps" kicker="Follow in order" action={
          <span className="btn small"><Clock3 size={12} />{protocol.estimatedMinutes} min</span>
        }>
          <ol className="checkup-steps">
            {protocol.steps.map((step, i) => (
              <li key={i}><span className="checkup-step-num">{i + 1}</span><span>{step}</span></li>
            ))}
          </ol>
          <div className="checkup-pass-box">
            <b>Pass criteria</b>
            <p>{protocol.passCriteria}</p>
          </div>
        </Card>

        <Card title="Quick links" kicker="Open while running the checkup">
          <div className="checkup-links">
            {protocol.links.map((l) => (
              <a key={l.href} className="checkup-link-card" href={l.href} target="_blank" rel="noopener noreferrer">
                <Link2 size={16} />
                <span>
                  <b>{l.label}</b>
                  <span className="cell-sub">{l.href.replace(/^https?:\/\//, "")}</span>
                </span>
                <ExternalLink size={14} />
              </a>
            ))}
          </div>
        </Card>
      </div>

      <div className="stack">
        <Card
          title="Record checkup result"
          kicker="Evidence for later analysis"
          className="checkup-form-card"
        >
          <label className="form-label">
            Session ID <span className="optional">(optional but recommended)</span>
            <input
              className="form-control"
              value={sessionId}
              onChange={(e) => setSessionId(e.target.value)}
              placeholder="e.g. 8f4989a2-… or short id from admin"
              autoComplete="off"
            />
          </label>
          <label className="form-label">
            What you saw / what you did
            <textarea
              className="form-control checkup-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Describe observations, unexpected behavior, screenshots notes, agent used, environment…"
              rows={6}
            />
          </label>
          {error && <div className="checkup-error">{error}</div>}
          <div className="checkup-cta-row">
            <button
              type="button"
              className="btn checkup-pass-btn"
              disabled={!!saving}
              onClick={() => void submit("pass")}
            >
              {saving === "pass" ? <Loader2 className="spin" /> : <CheckCircle2 />}
              Pass
            </button>
            <button
              type="button"
              className="btn checkup-fail-btn"
              disabled={!!saving}
              onClick={() => void submit("fail")}
            >
              {saving === "fail" ? <Loader2 className="spin" /> : <XCircle />}
              Fail
            </button>
          </div>
          <p className="checkup-cta-hint">
            Fail marks this protocol <b>red</b> until a newer Pass is recorded. Multiple runs are kept for stats.
          </p>
        </Card>

        <Card
          title="Checkup history"
          kicker={loading ? "Loading…" : `${results.length} saved`}
          action={<button type="button" className="btn small" onClick={() => void load()}><ClipboardCheck />Reload</button>}
        >
          <div className="checkup-history">
            {results.map((r) => {
              const isFocused = focusResultId === r.resultId;
              return (
                <div
                  key={r.resultId}
                  id={`checkup-result-${r.resultId}`}
                  ref={isFocused ? focusedRowRef : undefined}
                  className={`checkup-history-row ${r.status}${isFocused ? " is-focused" : ""}`}
                >
                  <div className="checkup-history-top">
                    <Pill tone={r.status === "pass" ? "green" : "red"}>
                      {r.status === "pass" ? "Pass" : "Fail"}
                    </Pill>
                    <span className="checkup-history-time">
                      {new Date(r.createdAt).toLocaleString()}
                    </span>
                    <button
                      type="button"
                      className="icon-button"
                      aria-label="Copy result link"
                      title="Copy deep link to this result"
                      onClick={() => void copyResultLink(r.resultId)}
                    >
                      <Link2 size={14} />
                    </button>
                    <button
                      type="button"
                      className="icon-button"
                      aria-label="Delete result"
                      onClick={() => void removeResult(r.resultId)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  {r.sessionId && (
                    <Link className="checkup-session-link" href={`/sessions/${r.sessionId}`}>
                      Session {r.sessionId}
                    </Link>
                  )}
                  <p>{r.notes}</p>
                  {r.createdBy && <span className="cell-sub">by {r.createdBy}</span>}
                </div>
              );
            })}
            {!loading && !results.length && (
              <div className="empty-state">No results yet for this protocol.</div>
            )}
          </div>
        </Card>
      </div>
    </div>

    {toast && (
      <div className="toast">
        <span className="toast-icon">✓</span>
        <span>
          <b>{toast}</b>
          {toast.startsWith("Recorded") && <span>Saved to DynamoDB for analysis.</span>}
          {toast === "Result link copied" && <span>Paste in a new tab to open this result.</span>}
        </span>
      </div>
    )}
  </>;
}
